import { useState } from 'react';
import { Check } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { EditModal } from '@/components/ui/edit-modal';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import type { Player } from '@/types';
import type { EloCategory } from '@/components/tournament/models/elo.model';
import { findCategoryByElo } from '@/components/tournament/utils/get-category-by-elo';

interface ChangeCategorySheetProps {
  open: boolean;
  /** Stays set after closing, so the panel keeps its content while it slides out. */
  player: Player | null;
  /**
   * Category this page just moved the player to. Wins over the one derived from their ELO,
   * which stays stale until the tournament refetch lands (or for good if it failed).
   */
  movedTo?: EloCategory;
  categories: EloCategory[];
  sportUid: string;
  onClose: () => void;
  /** Rejects when the change failed; the sheet then stays open for a retry. */
  onConfirm: (category: EloCategory) => Promise<void>;
}

/** Bottom sheet on phones; from `sm` up, the same edit modal as the rest of the tournament edits. */
export function ChangeCategorySheet({
  open,
  player,
  movedTo,
  categories,
  sportUid,
  onClose,
  onConfirm,
}: ChangeCategorySheetProps) {
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // Every opening starts with a clean selection (reset during render, not in an effect).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSelectedId(null);
  }

  // Highest category first, so the list reads like the ranking ladder.
  const sortedCategories = [...categories].sort(
    (a, b) => b.minimumElo - a.minimumElo,
  );

  const sportRanking = player?.rankings?.find((r) => r.sport?.uid === sportUid);
  const currentCategory =
    movedTo ??
    (sportRanking ? findCategoryByElo(categories, sportRanking.elo) : null);
  const selected =
    categories.find((category) => category.id === selectedId) ?? null;

  // Closing while the request is in flight would hide its outcome.
  const requestClose = () => {
    if (!saving) onClose();
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onConfirm(selected);
    } catch {
      // The hook already showed the error toast; keep the sheet open to retry.
    } finally {
      setSaving(false);
    }
  };

  const subtitle =
    player && currentCategory
      ? `${player.name} · hoy juega en ${currentCategory.name}`
      : player?.name;

  const options = (
    <>
      <div className="mt-4 flex flex-col gap-2">
        {sortedCategories.map((category) => {
          const isCurrent = category.id === currentCategory?.id;
          const isSelected = category.id === selectedId;
          return (
            <button
              key={category.id}
              type="button"
              aria-pressed={isSelected}
              disabled={isCurrent || saving}
              onClick={() => setSelectedId(category.id)}
              className={cn(
                'flex min-h-11 w-full items-center justify-between rounded-lg border px-4 py-2 text-left text-sm',
                isSelected
                  ? 'border-primary bg-primary/10 font-semibold text-primary ring-1 ring-primary'
                  : 'border-border bg-background hover:bg-muted',
                isCurrent &&
                  'cursor-not-allowed border-dashed bg-transparent text-muted-foreground opacity-60 hover:bg-transparent',
                saving && !isCurrent && 'opacity-60',
              )}
            >
              <span>
                {category.name}
                {isCurrent && ' · actual'}
              </span>
              {isSelected && <Check className="h-4 w-4" />}
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Lo podés volver a cambiar hasta que juegue un torneo de otro
        organizador.
      </p>
    </>
  );

  if (isDesktop) {
    return (
      <EditModal
        isOpen={open}
        onClose={requestClose}
        title="Cambiar categoría"
        onSave={handleConfirm}
        isLoading={saving}
        disabled={!selected}
      >
        {player && (
          <div className="py-2">
            <p className="text-sm text-muted-foreground">{subtitle}</p>
            {options}
          </div>
        )}
      </EditModal>
    );
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) requestClose();
      }}
    >
      <SheetContent
        side="bottom"
        className="max-h-[85vh] overflow-y-auto rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      >
        {player && (
          <>
            <SheetHeader>
              <SheetTitle>Cambiar categoría</SheetTitle>
              <SheetDescription>{subtitle}</SheetDescription>
            </SheetHeader>

            {options}

            <div className="mt-4 flex flex-col-reverse gap-2">
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={saving}
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className="min-h-11"
                disabled={!selected || saving}
                onClick={handleConfirm}
              >
                {selected ? `Pasar a ${selected.name}` : 'Elegí una categoría'}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
